import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { useClient } from 'urql';
import { fromArray, pipe, subscribe } from 'wonka';

import type {
    IWalletUpdatedSubscription,
    IWalletUpdatedSubscriptionVariables,
} from '../graphql/wallet.graphql';
import { WalletUpdatedDocument } from '../graphql/wallet.graphql';

/**
 * Hook that returns true/false denoting whether the user is idle
 * or not on the site. It checks mouse movements, touch events
 * @param time
 * @returns
 */
export function useIdle(time: number, callback?: (isIdle: boolean) => void) {
    const idleRef = useRef(false);
    const client = useClient();
    const auth = useAuth();
    const userId = auth.user?.profile.user_profile_id as string | undefined;
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        let timeoutId: number | null = null;

        const handleEvent = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            if (idleRef.current !== false) {
                callbackRef.current?.(false);
            }
            idleRef.current = false;

            timeoutId = setTimeout(() => {
                if (idleRef.current !== true) {
                    callbackRef.current?.(true);
                }
                idleRef.current = true;
            }, time);
        };

        // We don't want to consider the user inactive if there are wallet
        // events coming in. This is to avoid geolocation token updates from
        // causing the users gaming session to end while in gameplay.
        // (This is a workaround, ideally we can come up with a better solution)
        const subscription = pipe(
            userId
                ? client.subscription<
                      IWalletUpdatedSubscription,
                      IWalletUpdatedSubscriptionVariables
                  >(WalletUpdatedDocument, {
                      userProfileId: userId,
                  })
                : fromArray([]),
            subscribe(handleEvent),
        );

        document.addEventListener('mousemove', handleEvent, { passive: true });
        document.addEventListener('keydown', handleEvent, { passive: true });
        document.addEventListener('touchstart', handleEvent, { passive: true });
        document.addEventListener('touchend', handleEvent, { passive: true });
        document.addEventListener('scroll', handleEvent, { passive: true });

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            subscription.unsubscribe();
            document.removeEventListener('mousemove', handleEvent);
            document.removeEventListener('keydown', handleEvent);
            document.removeEventListener('touchstart', handleEvent);
            document.removeEventListener('touchend', handleEvent);
            document.removeEventListener('scroll', handleEvent);
        };
    }, [time, client, userId]);

    return idleRef.current;
}
